"use client";
import { apiRemote } from "@/api";
import { IUserRoundStatus } from "@/api/thoughtcocreate-api";
import { useThoughtCoCreate } from "@/contexts/ThoughtCoCreateContext";
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Typography,
  Button,
  Container,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  ListItemButton,
  useTheme,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";

const DRAWER_WIDTH = 280;

const STATUS_DRAFT = process.env.STATUS_DRAFT;
const STATUS_SUBMITTED = process.env.STATUS_SUBMITTED;
const STATUS_NOT_STARTED = process.env.STATUS_NOT_STARTED;
const STATUS_IN_PROGRESS = process.env.STATUS_IN_PROGRESS;
const STATUS_ENDED = process.env.STATUS_ENDED;

const statusLabels: Record<number, string> = {
  [Number(STATUS_DRAFT)]: "Draft",
  [Number(STATUS_SUBMITTED)]: "Submitted",
  [Number(STATUS_NOT_STARTED)]: "Not started",
  [Number(STATUS_IN_PROGRESS)]: "In progress",
  [Number(STATUS_ENDED)]: "Ended",
};

interface SubmissionCardProps {
  part_name: string;
  status: number;
  due_date: string;
  part_id: number;
  part_number: number;
}

const addOneDay = (date: Date) => {
  const newDate = new Date(date);
  newDate.setDate(newDate.getDate() + 1);
  return newDate;
};

const SubmissionCard = ({
  part_name,
  status,
  due_date,
  part_id,
  part_number,
}: SubmissionCardProps) => {
  const isPastDueDate = new Date(due_date) > new Date();
  const navigate = useNavigate();
  const dueDate = new Date(due_date);
  const today = new Date();
  const isDueToday =
    dueDate.getFullYear() === today.getFullYear() &&
    dueDate.getMonth() === today.getMonth() &&
    dueDate.getDate() === today.getDate();

  const isPastDue = today > dueDate;

  const handleClick = () => {
    navigate(`../deliverable/${part_id}`);
  };

  return (
    <Card
      variant="outlined"
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        "& .MuiCardContent-root": {
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <CardContent>
        <Typography variant="h6" component="h3" gutterBottom>
          {part_name}
        </Typography>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Status: {statusLabels[status] || "Unknown"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Due: {due_date}
          </Typography>
        </Box>
        <Box sx={{ mt: "auto" }}>
          <Button
            variant="contained"
            size="small"
            sx={{
              bgcolor: "black",
              "&:hover": {
                bgcolor: "grey.900",
              },
            }}
            // disabled={
            //   part_number !== 4
            //     ? today >= addOneDay(dueDate) && status !== Number(STATUS_SUBMITTED)
            //     : !isDueToday && status !== Number(STATUS_SUBMITTED)
            // }
            disabled={
              today >= addOneDay(dueDate) && status !== Number(STATUS_SUBMITTED)
            }
            onClick={handleClick}
          >
            {status === Number(STATUS_SUBMITTED) ? "View" : "Edit/Continue"}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default function SubmissionsDashboard() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  const { userCompetition, setUserCompetition, userRound, setUserRound, userRoundParts, setUserRoundParts, setAiUsage } = useThoughtCoCreate();

  const completedCount = userRoundParts?.filter(
    (submission) => submission.status === Number(STATUS_SUBMITTED)
  ).length;
  const totalCount = userRoundParts?.length;
  const progressPercentage =
    totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  const [loading, setLoading] = useState<boolean>(false);

  const loadUserRound = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiRemote.getUserRoundStatus();
      if (response?.competition) {
        setUserCompetition(response.competition);
      }
      if (response?.rounds) {
        setUserRound(response.rounds);
      }
      if (response?.round_parts) {
        setUserRoundParts(response.round_parts);
      }
      if (response?.ai_usage) {
        setAiUsage(response.ai_usage);
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (userRound.length <= 0) {
      //   navigate(`..`);
      loadUserRound();
    }
  }, [userRound, userRoundParts, userCompetition]);

  return (
    <Box
      sx={{
        p: 3,
        bgcolor: "white",
        borderRadius: 2,
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        flex: 1,
        height: "100%",
      }}
    >
      {loading ? (
        <Box
          sx={{
            p: 3,
            bgcolor: "white",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            bgcolor: "background.default",
          }}
        >
          <Container maxWidth="lg">
            <Typography
              variant="h4"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: "bold",
                mb: 1,
              }}
            >
              My ThoughtCoCreate Submissions
            </Typography>

            <Typography
              variant="subtitle1"
              color="text.secondary"
              sx={{ mb: 4 }}
            >
              Track your progress for D1–D4
            </Typography>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                Overall Progress
              </Typography>
              <Box sx={{ width: "100%", mb: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={progressPercentage}
                  sx={{
                    height: 10,
                    borderRadius: 5,
                    bgcolor: "grey.200",
                    "& .MuiLinearProgress-bar": {
                      bgcolor: "black",
                    },
                  }}
                />
              </Box>
              <Typography variant="body2" color="text.secondary">
                {completedCount} of {totalCount} deliverables completed
              </Typography>
            </Box>

            <Grid container spacing={3}>
              {userRoundParts.map((submission) => (
                <Grid item xs={12} sm={6} key={submission.part_name}>
                  <SubmissionCard {...submission} />
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      )}
    </Box>
  );
}
